import { Recipe } from '$tablet-app/src/app/recipes/types';
import { doStringArraysIntersect } from '$tablet-app/src/app/utils/arrays';

export const fish = ['Fish', 'Salmon', 'Tuna', 'Pescatarian'].map((food) =>
  food.toLowerCase()
);
export const nonPescatarian = [
  'Meat',
  'Pork',
  'Poultry',
  'Red meat',
  'Lamb',
  'Chicken',
  'Bacon',
  'Ham',
  'Beef',
].map((food) => food.toLowerCase());

export const nonVegetarian = [...nonPescatarian, ...fish];
export const nonVegan = [
  ...nonVegetarian,
  'Dairy',
  'Eggs',
  'Yoghurt',
  'Halloumi',
  'Cheese',
].map((food) => food.toLowerCase());

export const getRecipeIngredients = (recipe: Recipe): string[] => {
  const mainIngredient = recipe.mainIngredient?.label
    .replace(/[^\w\s]|_/g, '')
    .toLowerCase()
    .trim();

  const ingredientLabels = recipe.ingredients
    .map((ingr) =>
      ingr.name
        .replace(/[^\w\s]|_/g, '')
        .toLowerCase()
        .trim()
        .split(' ')
    )
    .flat();
  const allIngredients = [mainIngredient, ...ingredientLabels] as string[];
  return allIngredients;
};

export const getRecipeDietaryBalance = (
  dietBalanceAsDiscreteType: boolean,
  allIngredients: string[],
  sDiets: string[]
) => {
  const isVegan =
    !doStringArraysIntersect(nonVegan, allIngredients) ||
    sDiets.includes('vegan');
  const isVegetarian =
    !doStringArraysIntersect(nonVegetarian, allIngredients) ||
    sDiets.includes('vegetarian');
  const isPescatarian =
    !doStringArraysIntersect(nonPescatarian, allIngredients) ||
    sDiets.includes('pescatarian');

  const mainIngredient = allIngredients[0];
  const isMainlyFish = fish.includes(mainIngredient);
  const isMainlyMeat = nonPescatarian.includes(mainIngredient);

  const veganVal = Number(!isVegan && 1);
  const vegetarianVal = Number(!isVegetarian && 1);
  const pescatarianVal = Number(!isPescatarian && 1);
  const extraFishVal = Number(isMainlyFish && 1);
  const extraMeatVal = Number(isMainlyMeat && 1);

  const mapDietBalanceToFactor =
    veganVal + vegetarianVal + pescatarianVal + extraFishVal + extraMeatVal;

  return mapDietBalanceToFactor;
};

export const getRecipesFreeOfFoods = (specialDiets: string[]) => {
  const specialDietsFree = specialDiets.filter((diet) =>
    diet.toLowerCase().includes('free')
  );
  const dietsFreeOfFoods = specialDietsFree.map((dietFree) =>
    dietFree.toLowerCase().replace('free', '').trim()
  );
  return dietsFreeOfFoods;
};

export const getRecipesLowInFoods = (specialDiets: string[]) => {
  const specialDietsLow = specialDiets.filter((diet) =>
    diet.toLowerCase().includes('low ')
  );
  const dietsLowInFoods = specialDietsLow.map((dietLow) =>
    dietLow.toLowerCase().replace('low ', '').trim()
  );
  return dietsLowInFoods;
};

export const doesRecipeExcludeFoods = (
  recipeIngredients: string[],
  dietsFreeOfFood: string[],
  excludedFoods: string[]
) => {
  const isFreeOfFoodByIngredient = !doStringArraysIntersect(
    excludedFoods,
    recipeIngredients
  );

  let foodCompatible = true;

  excludedFoods.forEach((excludedFood) => {
    const dietFreeOfFood = dietsFreeOfFood.includes(
      excludedFood.toLowerCase().trim()
    );
    if (!dietFreeOfFood) {
      foodCompatible = false;
      return;
    }
  });

  const recipeExcludesFoods = foodCompatible && isFreeOfFoodByIngredient;

  return recipeExcludesFoods;
};

export const isRecipeWithinUsersTolerances = (
  recipeAllergens: string[],
  recipeSpecialDiets: string[],
  elementaryIngredients: string[],
  userAllergies: string[],
  userTotalAvoidances: string[],
  userPartialAvoidances: string[]
) => {
  const evaluateEachIngredient = false;
  if (!evaluateEachIngredient) {
    elementaryIngredients = [];
  }

  const recipeFreeOfFoods = getRecipesFreeOfFoods(recipeSpecialDiets);

  const recipeAllergyFree =
    !doStringArraysIntersect(elementaryIngredients, userAllergies) &&
    !doStringArraysIntersect(userAllergies, recipeAllergens);

  if (!recipeAllergyFree) return false;

  const recipeAvoidanceFree = doesRecipeExcludeFoods(
    elementaryIngredients,
    recipeFreeOfFoods,
    userTotalAvoidances
  );

  if (!recipeAvoidanceFree) return false;

  const recipeLowInFoods = [
    ...recipeFreeOfFoods,
    ...getRecipesLowInFoods(recipeSpecialDiets),
  ];

  const recipePartialAvoidanceCompatible = doesRecipeExcludeFoods(
    elementaryIngredients,
    recipeLowInFoods,
    userPartialAvoidances
  );

  if (!recipePartialAvoidanceCompatible) return false;

  return true;
};

export const mapAllergensToRecipes = (
  recipes: Recipe[],
  allergenOptions: any[]
) => {
  return recipes.map((recipe) => {
    if (allergenOptions && !recipe.allergens) {
      recipe.allergens = (recipe.allergyIDs as string[])
        .map((allergyID) =>
          allergenOptions.find(
            (allergy) => allergy.id === (allergyID as string)
          )
        )
        .map((allergen) => allergen.label);
    } else {
      recipe.allergens = [];
    }
    return recipe;
  });
};
